import { AlertTriangle, KeyRound, Mail, Minus, Plus, Save, UserRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { useAuth } from '@/auth/AuthProvider';
import { useStepUp } from '@/auth/StepUp';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { Cargando, ErrorCarga } from '../curso/componentes';

import {
  cambiarContrasena,
  cambiarCorreo,
  eliminarCuenta,
  useGuardarPerfil,
  useOpcionesPerfil,
  usePerfil,
  useUniversidades,
  type AccountProfile,
  type ProfileInput,
  type ProfileOptions,
} from './queries';

const esMexico = (p: string) =>
  p
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toUpperCase() === 'MEXICO';

/** Mi cuenta (port de `inicio/cuenta`): "Mi perfil" y "Mi acceso". */
export function CuentaPage() {
  const perfil = usePerfil();
  const opciones = useOpcionesPerfil();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'acceso' ? 'acceso' : 'perfil';
  if (perfil.isPending || opciones.isPending) return <Cargando alto="h-96" />;
  if (perfil.error) return <ErrorCarga error={perfil.error} />;
  if (opciones.error) return <ErrorCarga error={opciones.error} />;
  const falta = perfil.data.needs.profile || perfil.data.needs.phone || perfil.data.needs.realEmail;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">Mi cuenta</span>
      </h1>
      {falta && (
        <p className="panel-vidrio flex items-start gap-3 border-l-4 border-l-amarillo-400 text-sm">
          <AlertTriangle className="mt-0.5 shrink-0 text-amarillo-texto" size={18} />
          {perfil.data.needs.realEmail
            ? 'Tu cuenta no tiene un correo real. Registra uno en "Mi acceso" para poder iniciar sesión sin Facebook.'
            : 'Completa tu información (incluido tu teléfono) para continuar con tu preparación.'}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-modulo inline-flex items-center gap-2"
          data-state={tab === 'perfil' ? 'active' : undefined}
          onClick={() => setParams({}, { replace: true })}
        >
          <UserRound size={16} /> Mi perfil
        </button>
        <button
          type="button"
          className="btn-modulo inline-flex items-center gap-2"
          data-state={tab === 'acceso' ? 'active' : undefined}
          onClick={() => setParams({ tab: 'acceso' }, { replace: true })}
        >
          <KeyRound size={16} /> Mi acceso
        </button>
      </div>
      {tab === 'perfil' ? <Perfil p={perfil.data} o={opciones.data} /> : <Acceso p={perfil.data} />}
    </div>
  );
}

function Campo({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('block text-sm font-bold', className)}>
      {label}
      <div className="mt-1 font-normal">{children}</div>
    </label>
  );
}

function Perfil({ p, o }: { p: AccountProfile; o: ProfileOptions }) {
  const guardar = useGuardarPerfil();
  const navigate = useNavigate();
  const [f, setF] = useState<ProfileInput>(() => ({
    name: p.name,
    phone: p.profile.phone ?? '',
    school: p.profile.school ?? '',
    grade: p.profile.grade ?? 0,
    heardAbout: p.profile.heardAbout ?? '',
    specialty: p.profile.specialty ?? '',
    gender: p.profile.gender ?? 'S',
    birthDate: p.profile.birthDate ?? '',
    country: p.profile.country ?? '',
    state: p.profile.state,
    enarmAttempts: p.profile.enarmAttempts,
  }));
  const universidades = useUniversidades(f.school);
  const set = <K extends keyof ProfileInput>(k: K, v: ProfileInput[K]) =>
    setF((x) => ({ ...x, [k]: v }));
  const mexico = esMexico(f.country);
  const teniaPendiente = p.needs.profile || p.needs.phone;

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    guardar.mutate(
      { ...f, state: mexico ? f.state : null },
      {
        onSuccess: () => {
          toast.success('Guardamos tu información.');
          if (teniaPendiente) void navigate('/inicio');
        },
        onError: (er) => toast.error(mensajeDe(er)),
      },
    );
  };

  return (
    // Tarjeta con el ícono de cuenta asomado arriba al centro (`icon-top-middle` del legacy).
    <form onSubmit={enviar} className="card-cerebro mt-12 grid gap-4 sm:grid-cols-2">
      <img src="/img/cuenta/icon-cuenta.webp" alt="" className="card-cerebro__isotipo" />
      <p className="text-center font-extrabold text-azul-texto sm:col-span-2">
        Por favor ayúdanos a completar la información de tu registro
      </p>
      <Campo label="Nombre completo" className="sm:col-span-2">
        <input
          className="input-vidrio w-full"
          required
          minLength={3}
          maxLength={50}
          value={f.name}
          onChange={(e) => set('name', e.target.value)}
        />
      </Campo>
      <Campo label="Teléfono">
        <input
          className="input-vidrio w-full"
          type="tel"
          required
          value={f.phone}
          onChange={(e) => set('phone', e.target.value)}
          placeholder="55 1234 5678"
        />
      </Campo>
      <Campo label="Fecha de nacimiento">
        <input
          className="input-vidrio w-full"
          type="date"
          required
          value={f.birthDate}
          onChange={(e) => set('birthDate', e.target.value)}
        />
      </Campo>
      <Campo label="Género">
        <select
          className="input-vidrio w-full"
          value={f.gender}
          onChange={(e) => set('gender', e.target.value as ProfileInput['gender'])}
        >
          {o.genders.map((g) => (
            <option key={g.value} value={g.value}>
              {g.label}
            </option>
          ))}
        </select>
      </Campo>
      <Campo label="Especialidad que quieres">
        <select
          className="input-vidrio w-full"
          required
          value={f.specialty}
          onChange={(e) => set('specialty', e.target.value)}
        >
          <option value="">Elige una especialidad</option>
          {o.specialties.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </Campo>
      <Campo label="Escuela de medicina" className="sm:col-span-2">
        <input
          className="input-vidrio w-full"
          required
          minLength={3}
          maxLength={90}
          list="universidades"
          value={f.school}
          onChange={(e) => set('school', e.target.value)}
        />
        <datalist id="universidades">
          {(universidades.data ?? []).map((u) => (
            <option key={u} value={u} />
          ))}
        </datalist>
      </Campo>
      <Campo label="Promedio de la carrera">
        <input
          className="input-vidrio w-full"
          type="number"
          step="0.01"
          min={0}
          max={100}
          required
          value={f.grade || ''}
          onChange={(e) => set('grade', Number(e.target.value))}
        />
      </Campo>
      <Campo label="¿Cuántas veces has presentado el ENARM?">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-cs btn-vidrio h-10 w-10 p-0"
            aria-label="Menos"
            onClick={() => set('enarmAttempts', Math.max(f.enarmAttempts - 1, 0))}
          >
            <Minus size={16} />
          </button>
          <output className="w-8 text-center text-xl font-extrabold tabular">
            {f.enarmAttempts}
          </output>
          <button
            type="button"
            className="btn-cs btn-vidrio h-10 w-10 p-0"
            aria-label="Más"
            onClick={() => set('enarmAttempts', Math.min(f.enarmAttempts + 1, 20))}
          >
            <Plus size={16} />
          </button>
        </div>
      </Campo>
      <Campo label="País">
        <select
          className="input-vidrio w-full"
          required
          value={f.country}
          onChange={(e) => set('country', e.target.value)}
        >
          <option value="">Elige tu país</option>
          {o.countries.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </Campo>
      {mexico && (
        <Campo label="Estado">
          <select
            className="input-vidrio w-full"
            required
            value={f.state ?? ''}
            onChange={(e) => set('state', e.target.value)}
          >
            <option value="">Elige tu estado</option>
            {o.states.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Campo>
      )}
      <Campo label="¿Cómo te enteraste de nosotros?" className={mexico ? '' : 'sm:col-span-1'}>
        <select
          className="input-vidrio w-full"
          required
          value={f.heardAbout}
          onChange={(e) => set('heardAbout', e.target.value)}
        >
          <option value="">Elige una opción</option>
          {o.heardAbout.map((h) => (
            <option key={h}>{h}</option>
          ))}
        </select>
      </Campo>
      <div className="flex flex-wrap items-center justify-between gap-3 sm:col-span-2">
        <Link to="/licencias" className="text-sm font-bold text-azul-texto underline">
          Consultar mis licencias
        </Link>
        <button type="submit" className="btn-cs btn-exito" disabled={guardar.isPending}>
          <Save size={16} /> {guardar.isPending ? 'Guardando…' : 'Actualizar datos de perfil'}
        </button>
      </div>
    </form>
  );
}

function Acceso({ p }: { p: AccountProfile }) {
  const { proteger, dialogo } = useStepUp();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [correo, setCorreo] = useState(p.email ?? '');
  const [clave, setClave] = useState('');
  const [clave2, setClave2] = useState('');
  const [borrar, setBorrar] = useState(false);
  const [confirmacion, setConfirmacion] = useState('');
  const [ocupado, setOcupado] = useState<string | null>(null);

  const correr = async (cual: string, fn: () => Promise<unknown>, ok: string) => {
    setOcupado(cual);
    try {
      await proteger(fn);
      toast.success(ok);
      return true;
    } catch (e) {
      toast.error(mensajeDe(e));
      return false;
    } finally {
      setOcupado(null);
    }
  };

  return (
    <div className="space-y-6">
      <form
        className="panel-vidrio space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void correr(
            'correo',
            () => cambiarCorreo(correo),
            'Actualizamos tu correo. Te enviamos un aviso.',
          );
        }}
      >
        <h2 className="flex items-center gap-2 font-extrabold text-azul-texto">
          <Mail size={18} /> Correo
        </h2>
        <input
          className="input-vidrio w-full"
          type="email"
          required
          maxLength={60}
          value={correo}
          onChange={(e) => setCorreo(e.target.value)}
        />
        <p className="text-xs text-texto-3">
          Te pediremos un código de verificación para confirmar que eres tú.
        </p>
        <button
          type="submit"
          className="btn-cs btn-primario"
          disabled={ocupado !== null || correo.trim() === (p.email ?? '')}
        >
          Cambiar correo
        </button>
      </form>

      <form
        className="panel-vidrio space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void correr(
            'clave',
            () => cambiarContrasena(clave),
            'Actualizamos tu contraseña y cerramos tus otras sesiones.',
          ).then((ok) => {
            if (ok) {
              setClave('');
              setClave2('');
            }
          });
        }}
      >
        <h2 className="flex items-center gap-2 font-extrabold text-azul-texto">
          <KeyRound size={18} /> Contraseña
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            className="input-vidrio w-full"
            type="password"
            autoComplete="new-password"
            placeholder="Nueva contraseña"
            minLength={8}
            required
            value={clave}
            onChange={(e) => setClave(e.target.value)}
          />
          <input
            className="input-vidrio w-full"
            type="password"
            autoComplete="new-password"
            placeholder="Confírmala"
            required
            value={clave2}
            onChange={(e) => setClave2(e.target.value)}
          />
        </div>
        {clave2 && clave !== clave2 && (
          <p className="text-sm font-bold text-rojo-texto">Las contraseñas no coinciden.</p>
        )}
        <button
          type="submit"
          className="btn-cs btn-primario"
          disabled={ocupado !== null || clave.length < 8 || clave !== clave2}
        >
          Cambiar contraseña
        </button>
      </form>

      <section className="panel-vidrio space-y-3 border border-rojo-texto/40">
        <h2 className="flex items-center gap-2 font-extrabold text-rojo-texto">
          <AlertTriangle size={18} /> Eliminar mi cuenta
        </h2>
        <p className="text-sm text-texto-2">
          Se borran de forma permanente tu cuenta y tu avance. Los pagos realizados no son
          reembolsables. Si después quieres volver, escríbenos a contacto@consaludenarm.com.
        </p>
        <button type="button" className="btn-cs btn-peligro" onClick={() => setBorrar(true)}>
          Eliminar mi cuenta
        </button>
      </section>

      <Dialog open={borrar} onOpenChange={setBorrar}>
        <DialogContent>
          <DialogTitle>¿Eliminar tu cuenta?</DialogTitle>
          <DialogDescription>
            Esta acción no se puede deshacer. Escribe ELIMINAR para confirmar.
          </DialogDescription>
          <input
            className="input-vidrio mt-4 w-full"
            value={confirmacion}
            onChange={(e) => setConfirmacion(e.target.value)}
            aria-label="Escribe ELIMINAR"
          />
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className="btn-cs btn-vidrio" onClick={() => setBorrar(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-cs btn-peligro"
              disabled={confirmacion.trim().toUpperCase() !== 'ELIMINAR' || ocupado !== null}
              onClick={() => {
                void correr('borrar', eliminarCuenta, 'Eliminamos tu cuenta.').then(async (ok) => {
                  if (ok) {
                    await signOut().catch(() => undefined);
                    void navigate('/login', { replace: true });
                  }
                });
              }}
            >
              Eliminar definitivamente
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {dialogo}
    </div>
  );
}
